/**
 * `pending-shared-ui-primitives` v0.3 — `<Tabs />`.
 *
 * Token-driven tablist primitive. Closes the last slot of the v0
 * design doc (Button, Input, Modal, Card, Tabs). The 4 surfaces
 * that re-implement an inline `.login-tabs` / `.preflight-tabs` /
 * `.demo-tabs` / `.palette-tabs` pattern (FlowSourceModal,
 * PreflightPanel, ProductDemo, StepPalette + the freshly-extracted
 * flow-builder AuthTabHeader) can converge on this once the
 * v0.3.x migration sweep starts.
 *
 * Apps that consume this primitive MUST already import
 * `@rynora/ui/ui.css` once at the root (same as Button / Input /
 * Modal / Card).
 *
 * # API shape
 *
 * Compound-component pattern (matches our existing
 * `<Card><CardHeader/>…</Card>` shape and the Radix / Reach UI
 * convention agents will already know):
 *
 * ```tsx
 * <Tabs defaultValue="account">
 *   <TabList aria-label="Settings sections">
 *     <Tab value="account">Account</Tab>
 *     <Tab value="security">Security</Tab>
 *     <Tab value="billing" disabled>Billing</Tab>
 *   </TabList>
 *   <TabPanel value="account">…</TabPanel>
 *   <TabPanel value="security">…</TabPanel>
 *   <TabPanel value="billing">…</TabPanel>
 * </Tabs>
 * ```
 *
 * Controlled mode: pass `value` + `onValueChange`. Uncontrolled
 * mode: pass `defaultValue`. v0.3 supports both; the user picks.
 *
 * # ARIA contract (WAI-ARIA APG "Tabs" pattern)
 *
 *   - `<TabList>` emits `role="tablist"` with the caller's
 *     `aria-label` (or `aria-labelledby` — we forward arbitrary
 *     HTMLAttributes so the caller can do either).
 *   - `<Tab>` emits `role="tab"` + `aria-selected` + `aria-
 *     controls={panelId}` + `id={tabId}`. The `tabIndex` follows
 *     the "roving tabindex" pattern: 0 on the active tab, -1 on
 *     the rest. This means Tab key takes the user OUT of the
 *     tablist after the active tab — arrow keys do the in-list
 *     navigation. Matches WAI-ARIA APG and how Radix / Reach UI
 *     ship it.
 *   - `<TabPanel>` emits `role="tabpanel"` + `aria-labelledby=
 *     {tabId}` + `tabIndex={0}` (so panels are programmatically
 *     focusable for keyboard-only users) and is `hidden` when
 *     inactive (removed from the layout AND the AT tree).
 *
 * # Keyboard navigation (UI-06 in the design doc's regression table)
 *
 *   - ArrowLeft / ArrowRight: cycle through tabs, wrapping at the
 *     ends, skipping disabled tabs.
 *   - Home: jump to the first enabled tab.
 *   - End: jump to the last enabled tab.
 *   - Enter / Space: not bound — they're the native button
 *     activation keys; the browser already calls `onClick` for us.
 *
 * # v0.4 — vertical orientation
 *
 * Closes the v0.3 doc-block deferral. Pass `orientation="vertical"`
 * on the `<Tabs>` wrapper to render a sidebar-style tablist. The
 * primitive:
 *
 *   - Emits `aria-orientation="vertical"` on the `<TabList>` (WAI-
 *     ARIA APG requires this so AT software switches arrow-key
 *     expectations).
 *   - Swaps the arrow-key axis: ArrowUp / ArrowDown move between
 *     tabs (still wrapping at the ends, still skipping disabled
 *     tabs). ArrowLeft / ArrowRight are inert in vertical mode.
 *     Home / End still jump to first / last enabled tab.
 *   - Adds `rui-tabs--vertical` + `rui-tabs__list--vertical` class
 *     hooks so consumers can override the layout (default vertical
 *     layout: flex-column TabList, panels render alongside via the
 *     parent's flex / grid).
 *
 * Horizontal remains the default. Callers that omit `orientation`
 * are byte-identical to v0.3.
 *
 * # v0.4 explicitly does NOT do
 *
 *   - Manual activation mode. Today every consumer activates on
 *     click (= "automatic activation"). Some specs want
 *     focus-without-activate; we'd add `activationMode="manual"`
 *     in v0.5 if asked.
 *   - Drag-to-reorder. Not in the design doc.
 *   - Lazy panel rendering. Every shipped tablist has its panels
 *     in the DOM at all times; adding `mountMode="lazy"` is
 *     trivial later but means an extra branch we don't need yet.
 */

import { createContext, forwardRef, useCallback, useContext, useId, useMemo, useState } from 'react';
import type { ButtonHTMLAttributes, HTMLAttributes, KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';

// ─── Context ──────────────────────────────────────────────────────

export type TabsOrientation = 'horizontal' | 'vertical';

interface TabsContextValue {
  /** Currently-active tab value. */
  value: string;
  /** Setter called when the user activates a tab. */
  setValue: (next: string) => void;
  /** Stable prefix used to generate `tab-${value}` + `panel-${value}` IDs. */
  idBase: string;
  /** Layout axis — drives `aria-orientation` + arrow-key axis on TabList. */
  orientation: TabsOrientation;
}

const TabsContext = createContext<TabsContextValue | null>(null);

function useTabsContext(component: string): TabsContextValue {
  const ctx = useContext(TabsContext);
  if (!ctx) {
    throw new Error(`<${component}> must be used inside a <Tabs> tree`);
  }
  return ctx;
}

/** Stable id helper so Tab + TabPanel agree on their cross-references. */
function tabId(base: string, value: string): string {
  return `${base}-tab-${value}`;
}

function panelId(base: string, value: string): string {
  return `${base}-panel-${value}`;
}

// ─── <Tabs /> ─────────────────────────────────────────────────────

export interface TabsProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Controlled active value. Provide `onValueChange` alongside it. */
  value?: string;
  /** Initial active value in uncontrolled mode. */
  defaultValue?: string;
  /** Called when the user activates a different tab. */
  onValueChange?: (next: string) => void;
  /**
   * Layout axis. Defaults to `'horizontal'`. When set to `'vertical'`:
   *   - TabList gains `aria-orientation="vertical"`,
   *   - arrow-key axis swaps to ArrowUp / ArrowDown,
   *   - the `rui-tabs--vertical` + `rui-tabs__list--vertical` class
   *     hooks let consumers re-layout the tablist as a sidebar.
   */
  orientation?: TabsOrientation;
  /** TabList + TabPanels. */
  children?: ReactNode;
}

export function Tabs(props: TabsProps) {
  const {
    value: controlledValue,
    defaultValue,
    onValueChange,
    orientation = 'horizontal',
    className,
    children,
    ...rest
  } = props;
  const reactId = useId();
  const [uncontrolledValue, setUncontrolledValue] = useState<string>(() => defaultValue ?? '');
  const isControlled = controlledValue !== undefined;
  const value = isControlled ? controlledValue : uncontrolledValue;

  const setValue = useCallback(
    (next: string) => {
      if (!isControlled) setUncontrolledValue(next);
      onValueChange?.(next);
    },
    [isControlled, onValueChange],
  );

  const ctx = useMemo<TabsContextValue>(
    () => ({ value, setValue, idBase: reactId, orientation }),
    [value, setValue, reactId, orientation],
  );

  const cls = ['rui-tabs', orientation === 'vertical' ? 'rui-tabs--vertical' : '', className].filter(Boolean).join(' ');
  return (
    <TabsContext.Provider value={ctx}>
      <div className={cls} {...rest}>
        {children}
      </div>
    </TabsContext.Provider>
  );
}

// ─── <TabList /> ──────────────────────────────────────────────────

export interface TabListProps extends HTMLAttributes<HTMLDivElement> {
  children?: ReactNode;
}

export function TabList(props: TabListProps) {
  const { className, children, onKeyDown, ...rest } = props;
  const ctx = useTabsContext('TabList');
  const vertical = ctx.orientation === 'vertical';
  const cls = ['rui-tabs__list', vertical ? 'rui-tabs__list--vertical' : '', className].filter(Boolean).join(' ');

  // Axis-specific previous / next keys. Horizontal: ←/→ (default).
  // Vertical: ↑/↓. Home / End are universal in both axes.
  const prevKey = vertical ? 'ArrowUp' : 'ArrowLeft';
  const nextKey = vertical ? 'ArrowDown' : 'ArrowRight';

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(e);
    if (e.defaultPrevented) return;

    const key = e.key;
    if (key !== prevKey && key !== nextKey && key !== 'Home' && key !== 'End') {
      return;
    }

    const list = e.currentTarget;
    // Filter to enabled tabs only. Roving-tabindex skips disabled
    // tabs both in keyboard nav and in the Tab key out-of-list jump.
    const tabs = Array.from(list.querySelectorAll<HTMLButtonElement>('[role="tab"]')).filter(
      (b) => !b.disabled && b.getAttribute('aria-disabled') !== 'true',
    );
    if (tabs.length === 0) return;

    const active = (document.activeElement as HTMLElement | null) ?? null;
    const currentIndex =
      active && tabs.includes(active as HTMLButtonElement) ? tabs.indexOf(active as HTMLButtonElement) : 0;

    let nextIndex = currentIndex;
    if (key === prevKey) nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    else if (key === nextKey) nextIndex = (currentIndex + 1) % tabs.length;
    else if (key === 'Home') nextIndex = 0;
    else if (key === 'End') nextIndex = tabs.length - 1;

    if (nextIndex !== currentIndex) {
      e.preventDefault();
      const target = tabs[nextIndex];
      target.focus();
      // Automatic activation: clicking the focused tab is what the
      // WAI-ARIA APG says for "automatic activation" tablists. We
      // ship that mode in v0.3.
      target.click();
    } else if (key === 'Home' || key === 'End') {
      // Even with a single-tab list, Home/End shouldn't bubble.
      e.preventDefault();
    }
  };

  return (
    <div
      role="tablist"
      aria-orientation={vertical ? 'vertical' : undefined}
      className={cls}
      onKeyDown={handleKeyDown}
      {...rest}
    >
      {children}
    </div>
  );
}

// ─── <Tab /> ──────────────────────────────────────────────────────

export interface TabProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'value'> {
  /** Value of this tab — must match the same `value` on the paired `<TabPanel>`. */
  value: string;
  children?: ReactNode;
}

export const Tab = forwardRef<HTMLButtonElement, TabProps>(function Tab(props, ref) {
  const { value, className, children, onClick, disabled, ...rest } = props;
  const ctx = useTabsContext('Tab');
  const selected = ctx.value === value;
  const cls = ['rui-tabs__tab', selected ? 'rui-tabs__tab--selected' : '', className].filter(Boolean).join(' ');

  return (
    <button
      ref={ref}
      type="button"
      role="tab"
      id={tabId(ctx.idBase, value)}
      aria-selected={selected}
      aria-controls={panelId(ctx.idBase, value)}
      tabIndex={selected ? 0 : -1}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      className={cls}
      onClick={(e) => {
        onClick?.(e);
        if (!e.defaultPrevented && !disabled) {
          ctx.setValue(value);
        }
      }}
      {...rest}
    >
      {children}
    </button>
  );
});

// ─── <TabPanel /> ─────────────────────────────────────────────────

export interface TabPanelProps extends HTMLAttributes<HTMLDivElement> {
  /** Value of this panel — must match the `<Tab value=…>` it pairs with. */
  value: string;
  children?: ReactNode;
}

export const TabPanel = forwardRef<HTMLDivElement, TabPanelProps>(function TabPanel(props, ref) {
  const { value, className, children, ...rest } = props;
  const ctx = useTabsContext('TabPanel');
  const active = ctx.value === value;
  const cls = ['rui-tabs__panel', className].filter(Boolean).join(' ');

  return (
    <div
      ref={ref}
      role="tabpanel"
      id={panelId(ctx.idBase, value)}
      aria-labelledby={tabId(ctx.idBase, value)}
      tabIndex={0}
      hidden={!active}
      className={cls}
      {...rest}
    >
      {active ? children : null}
    </div>
  );
});
